// ---------------------------------------------------------------------------
// Shared types for the Documents (certificate/template) module
// ---------------------------------------------------------------------------

export type ElementBase = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
};

export type TextAlign = "left" | "center" | "right";
export type FontWeight = "normal" | "bold";

export type TextStyle = {
  fontFamily: string;
  fontSize: number;
  fontWeight: FontWeight;
  italic: boolean;
  color: string;
  align: TextAlign;
};

export type WildcardKey = "user_name" | "user_email" | "user_id";

export const WILDCARD_LABELS: Record<WildcardKey, string> = {
  user_name: "{User Name}",
  user_email: "{User Email}",
  user_id: "{User ID}",
};

export type TextElement = ElementBase &
  TextStyle & {
    type: "text";
    content: string;
  };

export type WildcardElement = ElementBase &
  TextStyle & {
    type: "wildcard";
    wildcardKey: WildcardKey;
  };

export type LineElement = ElementBase & {
  type: "line";
  color: string;
};

export type TemplateElement = TextElement | WildcardElement | LineElement;

export type AccessMode = "open" | "restricted";

// Identifiers are always the institute's User ID / E_id (student roll number or
// employee ID) — never email — per the access-control identity convention.
// The granted list itself is fetched separately and paginated server-side
// (see AccessListPage below) — a template record only ever carries a count,
// never the full list, since the list is uncapped in size.
export type AccessConfig = {
  mode: AccessMode;
  allowedIdsCount: number;
};

export type TemplateAccess = {
  student: AccessConfig;
  faculty: AccessConfig;
};

export type TemplateRole = keyof TemplateAccess;

// No cap on how many IDs a role's access list can hold in total — this
// instead caps how many grant/revoke identifiers a single "Apply Changes"
// call may submit at once (mirrors Documents.schemas.js on the backend).
export const MAX_ACCESS_BATCH_SIZE = 2000;

// Page size for the access-control ID table — deliberately larger than the
// template list's PAGE_SIZE (10) since these are much lighter rows.
export const ACCESS_LIST_PAGE_SIZE = 25;

// Checkbox multi-select (persists across pages) is meant for "I can see a
// handful of specific people to remove" — not large-scale removal, which is
// what bulk-delete-by-file is for. Capped well under MAX_ACCESS_BATCH_SIZE
// purely so clicking hundreds of individual checkboxes doesn't become the
// accidental workaround for what should be a file upload.
export const MAX_SELECTION_SIZE = 100;

// Cursor-paginated (RDB has no OFFSET) — `ids` ordered by key, `nextCursor`
// is simply the last id on the page to pass back for the next one. When a
// `search` term is supplied to fetchAccessList, this instead reflects a
// single exact-match lookup (each id is unique within a role's bucket).
export type AccessListPage = {
  ids: string[];
  hasMore: boolean;
  nextCursor: string | null;
};

// Everything staged in one "Apply Changes" review, submitted as one call.
export type ApplyAccessChangesPayload = {
  mode?: AccessMode;
  grantIds?: string[];
  revokeIds?: string[];
  clearAll?: boolean;
};

export type BackgroundFit = "cover" | "contain" | "fill";

// focalPoint is a 0-100 percentage pair — which part of the image should
// stay visible/centered when `fit` crops or letterboxes it. Mirrors CSS
// `object-position` / `background-position` semantics.
export type FocalPoint = { x: number; y: number };

export type DocumentBackground = {
  url: string;
  width: number;
  height: number;
  fit: BackgroundFit;
  focalPoint: FocalPoint;
  // 0-100 — lets the image be faded so foreground text/wildcards stay
  // legible over busy photos, same idea as Canva/PowerPoint image opacity.
  opacity: number;
};

export const DEFAULT_FOCAL_POINT: FocalPoint = { x: 50, y: 50 };
export const DEFAULT_BACKGROUND_OPACITY = 100;

// Background upload constraints (mirrors the size-limit convention already
// used for other uploads in env.prod.tsx, e.g. MAX_ATTENDANCE_REPORT_SIZE).
// This is the final cap, enforced client-side (post-compression) AND
// server-side (Documents.service.js#uploadBackground) — belt and suspenders.
export const MAX_BACKGROUND_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
// Reject before even attempting to decode/compress client-side, so a wildly
// oversized file (e.g. a phone's 50MB RAW-ish photo) can't hang the tab.
export const MAX_ORIGINAL_BACKGROUND_UPLOAD_SIZE = 25 * 1024 * 1024; // 25MB
// Pixel cap on the long edge — bounds canvas/render/storage cost regardless
// of the source photo's resolution; 2500px is comfortably above print-DPI
// needs for anything up to A3 at 200dpi.
export const MAX_BACKGROUND_DIMENSION = 2500;
export const BACKGROUND_COMPRESSION_QUALITY = 0.9;
export const ACCEPTED_BACKGROUND_IMAGE_TYPES = [ "image/png", "image/jpeg", "image/webp" ];

export type DocumentTemplate = {
  id: string;
  name: string;
  description?: string;
  background: DocumentBackground;
  elements: TemplateElement[];
  access: TemplateAccess;
  isActive: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
};

// Cursor for the real backend's RDB-based pagination (see
// Functions3/functions/helpers/Documents/Documents.service.js#fetchTemplates)
// — RDB has no OFFSET, so "page N" isn't addressable; you can only ask for
// "the batch after this cursor."
export type TemplateCursor = { value: string; key: string };

export type FetchTemplatesRealParams = {
  search?: string;
  limit?: number;
  cursor?: TemplateCursor | null;
};

export type FetchTemplatesRealResult = {
  success: boolean;
  templates: DocumentTemplate[];
  hasMore: boolean;
  nextCursor: TemplateCursor | null;
};

export type CanvasPreset = {
  label: string;
  width: number;
  height: number;
};

export const CANVAS_PRESETS: CanvasPreset[] = [
  { label: "A4 Portrait", width: 794, height: 1123 },
  { label: "A4 Landscape", width: 1123, height: 794 },
  { label: "Letter Portrait", width: 816, height: 1056 },
  { label: "Letter Landscape", width: 1056, height: 816 },
];

export const SAMPLE_PREVIEW_USER = {
  user_name: "Jane Doe",
  user_email: "jane@uni.edu",
  user_id: "UID-0001",
};

export const FONT_FAMILIES = [
  "Inter",
  "Georgia",
  "Times New Roman",
  "Arial",
  "Courier New",
  "Playfair Display",
];
